<template>
  <v-container>
    <step-buttons-therapy
      class="px-0 my-3"
      :show-save-button="canSaveToS3"
      :is-saving="isSavingToS3"
      :can-save="canSaveToS3"
      @click-back="back"
      @click-next="submit"
      @click-save="$emit('save-to-s3')"
    />
    <therapy-card title="Passo a passo da terapia">
      <div class="mb-3">
        Se você realizou algum procedimento pré terapia, informe abaixo:
      </div>

      <label-icon icon="mode_comment" label="Comentários" bold />

      <v-textarea
        v-model="therapistComments"
        class="mt-3 text-area-custom"
        label="Comentários do terapeuta"
        placeholder="Informações adicionais (Protocolos, técnicas, procedimentos realizadas antes da terapia, exames complementares, comentários, etc). Este campo não será mostrado ao paciente."
        persistent-placeholder
        outlined
        rows="3"
      />
      <v-textarea
        v-model="patientComments"
        label="Comentários ao paciente"
        placeholder="Comentários gerais ao paciente (mostrado ao paciente)"
        persistent-placeholder
        outlined
        rows="3"
      />

      <p class="black--text subtitle-1 font-weight-regular">
        <v-icon>chevron_right</v-icon>
        Esses são os passos da terapia, de acordo com a sequência escolhida:
      </p>
      <v-divider />
      <v-list>
        <div v-for="(property, index) in sequence" :key="property.name">
          <div :class="`mb-3 subtitle-2 ${index !== 0 ? 'mt-5' : ''}`">
            <div class="subtitle-1 text-center my-3">
              <span>{{ property.propertyLabel }}: </span>
              <span class="font-weight-bold">{{ property.name }}</span>
            </div>
            <explanation-icon
              v-if="property.explanation"
              :name="property.name"
              :explanation="property.explanation"
            />
          </div>
          <step-item
            v-for="(step, indexStep) in property.steps"
            :key="`${step.slug || 'step'}-${indexStep}`"
            :title="step.title"
            :index="indexStep + 1"
            :first-word-bold="step.firstWordBold"
            :is-added="isStepAddedIntoDiagnosis(step)"
            class="elevation-2 mb-2 item-par"
            @click="openDetailDialog(step)"
          />
        </div>
      </v-list>
    </therapy-card>
    <step-buttons-therapy
      class="my-3"
      :show-save-button="canSaveToS3"
      :is-saving="isSavingToS3"
      :can-save="canSaveToS3"
      @click-back="back"
      @click-next="submit"
      @click-save="$emit('save-to-s3')"
    />
    <auto-scroll
      @start="scrollToEnd"
      @pause="pauseScroll"
      @resume="resumeScroll"
      @stop="stopScroll"
    />
    <dialogs />
    <div ref="lastComponent" />
  </v-container>
</template>

<script>
import VueScrollTo from "vue-scrollto";
import { SEQUENCE } from "@/domains/therapy/steps/mount-sequence/store/sequence/types";
import { SlugEnum } from "../../../mount-sequence/domain/slug-enum";
import { findByTrackingPoint } from "@/services/local/points-order-service";
export default {
  components: {
    StepItem: () =>
      import("@/domains/therapy/shared/presentation/components/StepItem"),
    Dialogs: () =>
      import("@/domains/therapy/shared/presentation/dialogs/Dialogs"),
    AutoScroll: () =>
      import(
        "@/domains/therapy/steps/execute/presentation/components/AutoScroll"
      ),
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    isSavingToS3: {
      type: Boolean,
      default: false,
    },
    canSaveToS3: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    cancelScroll: () => {},
    trackingPointsWithPares: {}, // Object<"trackingPointId-typeId", Set<parId>> — objeto plano para reatividade Vue 2
    scrollInterval: null,
    userScrollTimeout: null,
    currentPixelsPerFrame: 0,
  }),
  computed: {
    sequence() {
      const sequence = this.$store.state.sequence.sequence;
      const words = [
        "calcificação",
        "hipertrofia",
        "hiperplasia",
        "metaplasia",
        "hipotrofia",
        "fibrose",
        "infiltrado",
        "exsudato",
        "cisto / pólipo",
        "abscesso",
        "displasia",
        "metástase",
        "necrose",
      ];
      let firstWordCancerFound = false;
      let firstWordNeoplasiaFound = false;

      sequence.forEach((property) => {
        property.steps?.forEach((step) => {
          if (
            step.title.toLowerCase().startsWith("câncer") &&
            !firstWordCancerFound
          ) {
            firstWordCancerFound = true;
            step.firstWordBold = true;
          }
          if (
            step.title.toLowerCase().startsWith("neoplasia") &&
            !firstWordNeoplasiaFound
          ) {
            firstWordNeoplasiaFound = true;
            step.firstWordBold = true;
          }
          words.forEach((word) => {
            if (step.title.toLowerCase().includes(word)) {
              step.firstWordBold = true;
            }
          });
        });
      });

      return sequence;
    },
    preTherapyProcedures: {
      get() {
        return this.$store.state.therapy.therapy.preTherapyProcedures;
      },
      set(value) {
        this.$store.commit("therapy/setPreTherapyProcedures", value);
      },
    },
    therapistComments: {
      get() {
        return this.$store.state.diagnosis.diagnose?.therapistComments || "";
      },
      set(value) {
        this.$store.commit("diagnosis/setTherapistComments", value);
      },
    },
    patientComments: {
      get() {
        return this.$store.state.diagnosis.diagnose?.patientComments || "";
      },
      set(value) {
        this.$store.commit("diagnosis/setPatientComments", value);
      },
    },
  },
  watch: {
    "$store.state.autoScroll.selectedSpeed"() {
      // Se estiver rolando, a velocidade será recalculada a cada frame
      if (this.$store.state.autoScroll.isScrolling) {
        this.currentPixelsPerFrame =
          this.$store.getters["autoScroll/pixelsPerFrame"];
      }
    },
  },
  async created() {
    await this.preloadTrackingPointsData();
  },
  mounted() {
    window.addEventListener("wheel", this.handleUserScroll);
    window.addEventListener("touchmove", this.handleUserScroll);
  },
  beforeDestroy() {
    this.stopScroll();
    window.removeEventListener("wheel", this.handleUserScroll);
    window.removeEventListener("touchmove", this.handleUserScroll);
  },
  methods: {
    isStepAddedIntoDiagnosis(step) {
      const slug = step.slug;

      // 🛡️ Proteção: verifica se diagnose existe
      if (!this.$store.state.diagnosis.diagnose) {
        return false;
      }

      let items = [];

      switch (slug) {
        case SlugEnum.CHAKRAS:
          items = this.$store.state.diagnosis.diagnose.chakras || [];
          return items.some((item) => item.chakra.id === step.type.id);

        case SlugEnum.CROMOSSOMOS:
          items = this.$store.state.diagnosis.diagnose.chromosomes || [];
          return items.some((item) => item.chromosome.id === step.type.id);

        case SlugEnum.TERAPIAS_COMPLEMENTARES:
          items =
            this.$store.state.diagnosis.diagnose.complementarTherapies || [];
          return items.some(
            (item) => item.complementarTherapy.id === step.type.id
          );

        case SlugEnum.OLIGOELEMENTOS:
          items = this.$store.state.diagnosis.diagnose.oligoelements || [];
          const elements = step.type.oligoelements.map((el) => el.id);
          return items.some((item) =>
            elements.some((el) => el === item.oligoelement.id)
          );

        case SlugEnum.TIPOS_DE_FLUXO:
          items = this.$store.state.diagnosis.diagnose.flowTypes || [];
          return items.some((item) => item.flowType.id === step.type.id);

        case SlugEnum.ESPACOS_POTENCIAIS:
          items = this.$store.state.diagnosis.diagnose.potentialSpaces || [];
          return items.some((item) => item.potentialSpace.id === step.type.id);

        case SlugEnum.FENOMENOS_VASCULARES:
          items = this.$store.state.diagnosis.diagnose.vascularPhenomena || [];
          return items.some(
            (item) => item.vascularPhenomenon.id === step.type.id
          );

        case SlugEnum.FENOMENOS_TUMORAIS:
          items = this.$store.state.diagnosis.tumoralPhenomena?.added || [];
          return items.some(
            (item) => item.tumoralPhenomenon.id === step.type.id
          );

        case SlugEnum.RESERVATORIO:
        case SlugEnum.RASTREIO:
        default:
          items = this.$store.state.diagnosis.diagnose.pares || [];

          // Trata listas de aggregation
          if (step.additionalData && step.additionalData.aggregationPairs) {
            const aggregationPairs = step.additionalData.aggregationPairs.map(
              (par) => par.parId
            );
            return items.some((item) => {
              if (!item.par) {
                return false;
              }
              return aggregationPairs.includes(item.par.id);
            });
          }

          // Trata listas de trackingPoint
          if (step.trackingPoint) {
            const trackingPointId = step.trackingPoint.id;
            const typeId = step.additionalData?.typeId;

            // Se não tiver typeId, não consegue verificar corretamente
            if (!typeId) {
              return false;
            }

            return items.some((item) => {
              if (!item.par) return false;

              // Verificação direta: usa typeId e trackingPointId armazenados no par
              // (disponível em pares adicionados após a correção do mapper)
              if (item.par.typeId != null && item.par.trackingPointId != null) {
                return (
                  item.par.typeId === typeId &&
                  item.par.trackingPointId === trackingPointId
                );
              }

              // Fallback para pares legados (sem typeId/trackingPointId no diagnóstico):
              // usa o Map pré-carregado
              const key = `${trackingPointId}-${typeId}`;
              const availablePares =
                this.trackingPointsWithPares[key] || new Set();
              return availablePares.has(item.par.id);
            });
          }

          // Trata listas de Pares
          if (step.type) {
            return items.some((item) => {
              if (!item.par || !step.type) {
                return false;
              }
              return item.par.id === step.type.id;
            });
          }

          return false;
      }
    },
    openDetailDialog(step) {
      if (this.$store.state.autoScroll.isScrolling) {
        this.pauseScroll();
      }
      this.$store.dispatch(SEQUENCE.ACTIONS.OPEN_DETAIL_DIALOG, step);
    },
    closeTumoralPhenomenaDialog() {
      this.$store.commit(
        SEQUENCE.MUTATIONS.CHANGE_TUMORAL_PHENOMENA_DIALOG,
        false
      );
    },
    closeImpactionPointsDialog() {
      this.$store.commit(
        SEQUENCE.MUTATIONS.CHANGE_IMPACTION_POINTS_DIALOG,
        false
      );
    },
    submit() {
      this.$emit("next");
    },
    back() {
      this.$emit("back");
    },
    scrollToEnd() {
      this.stopScroll(); // Para qualquer rolagem anterior

      const el = this.$refs.lastComponent;
      if (!el) return;

      const scrollElement =
        document.scrollingElement || document.documentElement;
      if (!scrollElement) return;

      this.$store.commit("autoScroll/setIsScrolling", true);
      this.$store.commit("autoScroll/setIsPaused", false);

      const targetPosition =
        scrollElement.scrollHeight - scrollElement.clientHeight;

      this.scrollInterval = setInterval(() => {
        // Verifica se está pausado
        if (this.$store.state.autoScroll.isPaused) {
          return;
        }

        // Recalcula pixelsPerFrame a cada frame para capturar mudança de velocidade em tempo real
        const pixelsPerFrame = this.$store.getters["autoScroll/pixelsPerFrame"];
        const currentPosition = scrollElement.scrollTop;
        const remainingDistance = targetPosition - currentPosition;

        // Se chegou ao final, para a rolagem
        if (remainingDistance <= pixelsPerFrame) {
          this.stopScroll();
          return;
        }

        // Rola incrementalmente com a velocidade recalculada em tempo real
        scrollElement.scrollTop += pixelsPerFrame;
      }, 16); // ~60fps
    },

    stopScroll() {
      if (this.scrollInterval) {
        clearInterval(this.scrollInterval);
        this.scrollInterval = null;
      }
      this.$store.commit("autoScroll/setIsScrolling", false);
      this.$store.commit("autoScroll/setIsPaused", false);
    },

    pauseScroll() {
      this.$store.commit("autoScroll/setIsPaused", true);
    },

    resumeScroll() {
      this.$store.commit("autoScroll/setIsPaused", false);
      // Garante que a velocidade seja recalculada ao retomar
      this.currentPixelsPerFrame =
        this.$store.getters["autoScroll/pixelsPerFrame"];
    },

    handleUserScroll() {
      // Pausa automaticamente se estiver rolando e usuário tentar rolar manualmente
      const isScrolling = this.$store.state.autoScroll.isScrolling;
      const isPaused = this.$store.state.autoScroll.isPaused;

      if (isScrolling && !isPaused) {
        clearTimeout(this.userScrollTimeout);
        this.userScrollTimeout = setTimeout(() => {
          this.pauseScroll();
        }, 100);
      }
    },

    async preloadTrackingPointsData() {
      // Percorre toda a sequence para pré-carregar os pares de cada trackingPoint + tipo
      const sequence = this.$store.state.sequence.sequence;

      for (const property of sequence) {
        if (!property.steps || property.steps.length === 0) {
          continue;
        }

        for (const step of property.steps) {
          // Apenas processa steps que têm trackingPoint
          if (!step.trackingPoint || !step.trackingPoint.id) {
            continue;
          }

          const trackingPointId = step.trackingPoint.id;
          const typeId = step.additionalData?.typeId;

          // Se não tiver typeId, pula (não consegue criar a chave correta)
          if (!typeId) {
            continue;
          }

          // Cria a chave composta
          const key = `${trackingPointId}-${typeId}`;

          // Se já carregamos esta combinação, pula
          if (key in this.trackingPointsWithPares) {
            continue;
          }

          try {
            // Busca todos os pointsOrder para este trackingPoint
            const pointsOrder = await findByTrackingPoint([trackingPointId]);

            // Filtra apenas os pares do tipo específico e extrai os parIds únicos
            const parIds = new Set(
              pointsOrder
                .filter((po) => po.parId && po.par?.typeId === typeId)
                .map((po) => po.parId)
            );

            // Armazena no objeto com this.$set para que Vue 2 detecte a mudança e re-renderize
            this.$set(this.trackingPointsWithPares, key, parIds);
          } catch (error) {
            console.error(
              `Erro ao carregar pares do trackingPoint ${trackingPointId} tipo ${typeId}:`,
              error
            );
          }
        }
      }
    },
  },
};
</script>

<style>
.text-area-custom.v-textarea .v-text-field__slot textarea::placeholder {
  white-space: pre-line;
  word-break: break-word;
}
</style>
